import React from 'react';
import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';
import clsx from '@strava/ui/clsx';
import { Markup } from 'interweave';

import PromoCodeInput from './PromoCodeInput';
import Discounts from './Discounts';
import { getStyles as _getStyles } from '../../../utils/checkoutUtils.js';
import { useCheckoutContext } from '../../../state/CheckoutContext';

import styles from './PricingList.scss';

export const I18nPrefix = 'strava.subscriptions.checkout.summary.organic';

const renderPerMonthPrice = (price, currency) => (
  <Markup
    tagName="span"
    content={I18n.t(`${I18nPrefix}.per_month`, {
      class: 'price',
      price: Cldr.formatCurrency(price, currency)
    })}
  />
);

const renderPerYearPrice = (price, currency) => (
  <Markup
    tagName="span"
    content={I18n.t(`${I18nPrefix}.per_year`, {
      class: 'price',
      price: Cldr.formatCurrency(price, currency)
    })}
  />
);

export const PricingList = () => {
  const { checkoutEntityState } = useCheckoutContext();

  const {
    experiments,
    products,
    promoCodeInputVisibility: isPromoCodeInputVisible,
    pricing
  } = checkoutEntityState;
  const {
    yearlyTotal,
    monthlyTotal,
    billedToday: totalPrimaryPrice,
    billedTodayOther: totalSecondaryPrice,
    currency,
    discounts,
    duration
  } = pricing;
  const promotionApplied = !!products?.promotion?.code;
  const { shouldShowCheckoutRefresh } = experiments;
  const isYearlyDuration = duration === 'yearly';
  const hasDiscounts = discounts.length > 0;
  const shouldShowPromoInput = !promotionApplied && isPromoCodeInputVisible;

  const getStyles = (style) =>
    _getStyles(styles, style, shouldShowCheckoutRefresh);

  const totalPrimaryText = isYearlyDuration
    ? renderPerYearPrice(totalPrimaryPrice, currency)
    : renderPerMonthPrice(totalPrimaryPrice, currency);

  const totalSecondaryText = isYearlyDuration
    ? renderPerMonthPrice(totalSecondaryPrice, currency)
    : renderPerYearPrice(totalSecondaryPrice, currency);

  const subtotalText = isYearlyDuration
    ? renderPerYearPrice(yearlyTotal, currency)
    : renderPerMonthPrice(monthlyTotal, currency);

  return (
    <div className={clsx(getStyles('pricingList'))}>
      {hasDiscounts && !shouldShowCheckoutRefresh && <Discounts />}

      {shouldShowPromoInput && !shouldShowCheckoutRefresh && (
        <>
          <PromoCodeInput />
          <div className={styles.divider} />
        </>
      )}

      <div className={clsx(getStyles('totals'))}>
        <div data-testid="price-list-total-text">
          {I18n.t(
            `${I18nPrefix}.${
              shouldShowCheckoutRefresh ? 'billed_today' : 'total'
            }`
          )}
        </div>
        <div className="text-right">
          <div
            className={clsx(getStyles('totalPrimaryText'))}
            data-testid="price-list-total-primary-text"
          >
            {totalPrimaryText}
          </div>
          {!shouldShowCheckoutRefresh && (
            <div
              className={clsx(getStyles('totalSecondaryText'))}
              data-testid="price-list-total-secondary-text"
            >
              {totalSecondaryText}
            </div>
          )}
        </div>
      </div>
      {shouldShowCheckoutRefresh && (
        <>
          <div
            className={clsx(
              getStyles('priceRow'),
              getStyles('note'),
              hasDiscounts && getStyles('discounts')
            )}
          >
            <div>
              {I18n.t(
                `${I18nPrefix}.${
                  hasDiscounts ? 'billed_after_first_year' : 'renews'
                }`
              )}
            </div>
            {hasDiscounts && <div>{subtotalText}</div>}
          </div>
          {hasDiscounts && <Discounts />}
        </>
      )}
    </div>
  );
};

export default PricingList;
